/**
 * Impulso Digital - Offline Database (IndexedDB)
 * Gerencia armazenamento local para funcionamento offline
 */

import { isOfflinePhoto, uploadOfflinePhoto } from '../components/ImageUpload';
import { authHeaders } from './api';

const DB_NAME = 'impulso_digital_offline';
const DB_VERSION = 1;

// Stores
const STORES = {
  WORK_ORDERS: 'work_orders',
  MACHINES: 'machines',
  OPERATORS: 'operators',
  LOCATIONS: 'locations',
  PENDING_SYNC: 'pending_sync',
  CACHE_META: 'cache_meta'
};

let db = null;

/**
 * Inicializa o banco de dados IndexedDB
 */
export const initDB = () => {
  return new Promise((resolve, reject) => {
    if (db) {
      resolve(db);
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      console.error('Erro ao abrir IndexedDB:', request.error);
      reject(request.error);
    };

    request.onsuccess = () => {
      db = request.result;
      console.log('✅ IndexedDB inicializado');
      resolve(db);
    };

    request.onupgradeneeded = (event) => {
      const database = event.target.result;

      // Work Orders store
      if (!database.objectStoreNames.contains(STORES.WORK_ORDERS)) {
        const woStore = database.createObjectStore(STORES.WORK_ORDERS, { keyPath: 'id' });
        woStore.createIndex('status', 'status', { unique: false });
        woStore.createIndex('date', 'date', { unique: false });
        woStore.createIndex('synced', 'synced', { unique: false });
      }

      // Machines store
      if (!database.objectStoreNames.contains(STORES.MACHINES)) {
        const machinesStore = database.createObjectStore(STORES.MACHINES, { keyPath: 'id' });
        machinesStore.createIndex('status', 'status', { unique: false });
      }

      // Operators store
      if (!database.objectStoreNames.contains(STORES.OPERATORS)) {
        const operatorsStore = database.createObjectStore(STORES.OPERATORS, { keyPath: 'id' });
        operatorsStore.createIndex('status', 'status', { unique: false });
      }

      // Locations store
      if (!database.objectStoreNames.contains(STORES.LOCATIONS)) {
        const locationsStore = database.createObjectStore(STORES.LOCATIONS, { keyPath: 'id' });
        locationsStore.createIndex('status', 'status', { unique: false });
      }

      // Pending sync store (for offline created/updated items)
      if (!database.objectStoreNames.contains(STORES.PENDING_SYNC)) {
        const syncStore = database.createObjectStore(STORES.PENDING_SYNC, { keyPath: 'localId', autoIncrement: true });
        syncStore.createIndex('type', 'type', { unique: false });
        syncStore.createIndex('action', 'action', { unique: false });
        syncStore.createIndex('createdAt', 'createdAt', { unique: false });
      }

      // Cache metadata store
      if (!database.objectStoreNames.contains(STORES.CACHE_META)) {
        database.createObjectStore(STORES.CACHE_META, { keyPath: 'key' });
      }

      console.log('✅ IndexedDB schema criado');
    };
  });
};

/**
 * Operações genéricas no banco
 */
const getStore = (storeName, mode = 'readonly') => {
  if (!db) throw new Error('Database não inicializado');
  const transaction = db.transaction(storeName, mode);
  return transaction.objectStore(storeName);
};

const promisifyRequest = (request) => {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

/**
 * Salvar dados em um store
 */
export const saveToStore = async (storeName, data) => {
  await initDB();
  const store = getStore(storeName, 'readwrite');
  
  if (Array.isArray(data)) {
    const promises = data.map(item => promisifyRequest(store.put(item)));
    return Promise.all(promises);
  }
  
  return promisifyRequest(store.put(data));
};

/**
 * Buscar todos os dados de um store
 */
export const getAllFromStore = async (storeName) => {
  await initDB();
  const store = getStore(storeName);
  return promisifyRequest(store.getAll());
};

/**
 * Buscar item por ID
 */
export const getById = async (storeName, id) => {
  await initDB();
  const store = getStore(storeName);
  return promisifyRequest(store.get(id));
};

/**
 * Deletar item por ID
 */
export const deleteById = async (storeName, id) => {
  await initDB();
  const store = getStore(storeName, 'readwrite');
  return promisifyRequest(store.delete(id));
};

/**
 * Limpar um store
 */
export const clearStore = async (storeName) => {
  await initDB();
  const store = getStore(storeName, 'readwrite');
  return promisifyRequest(store.clear());
};

// ============================================
// FUNÇÕES ESPECÍFICAS PARA O APP
// ============================================

/**
 * Janela máxima (em dias) de O.S. ABERTAS mantidas no cache offline.
 * Abertas mais antigas que isso são descartadas e removidas do IndexedDB.
 * Fechadas são preservadas (servem de histórico para contexto em campo).
 */
const OFFLINE_OPEN_WINDOW_DAYS = 60;

/**
 * Espelha a lista COMPLETA de O.S. vinda do servidor no cache local (IndexedDB).
 * - Salva todas as O.S. elegíveis (fechadas + abertas dentro da janela de 60 dias)
 * - Remove do cache entradas obsoletas (abertas >60d) e órfãs (removidas/alteradas
 *   no servidor), preservando O.S. criadas offline ainda não sincronizadas.
 * Deve ser chamada SOMENTE com a listagem completa (sem status_filter).
 */
export const mirrorWorkOrdersCache = async (orders) => {
  if (!Array.isArray(orders)) return;
  await initDB();
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - OFFLINE_OPEN_WINDOW_DAYS);
  const cutoffStr = cutoff.toISOString().split('T')[0];

  const eligible = orders.filter((o) => {
    if (o.status !== 'open') return true;
    const d = (o.date || '').slice(0, 10);
    return d >= cutoffStr;
  });
  await saveToStore(STORES.WORK_ORDERS, eligible.map((o) => ({ ...o, synced: true })));

  const serverIds = new Set(orders.map((o) => o.id));
  const cached = await getAllFromStore(STORES.WORK_ORDERS);
  for (const c of cached) {
    const isStaleOpen = c.status === 'open' && (c.date || '').slice(0, 10) < cutoffStr;
    const isOrphan = !serverIds.has(c.id) && !c.createdOffline;
    if (isStaleOpen || isOrphan) {
      await deleteById(STORES.WORK_ORDERS, c.id);
    }
  }

  await saveToStore(STORES.CACHE_META, {
    key: 'lastSync',
    timestamp: new Date().toISOString()
  });
  // Segregação por tenant: registra a qual empresa o cache pertence
  await saveToStore(STORES.CACHE_META, {
    key: 'cacheTenantId',
    tenantId: localStorage.getItem('selectedTenantId') || ''
  });
};

/**
 * Verifica se o cache local de O.S. pertence ao tenant ATIVO da sessão atual.
 * Impede que dados de outra empresa (sessão anterior do Master) sejam exibidos.
 */
export const cacheMatchesCurrentTenant = async () => {
  try {
    await initDB();
    const meta = await getById(STORES.CACHE_META, 'cacheTenantId');
    if (!meta || !meta.tenantId) return true; // cache antigo sem marcação: tolera (usuário comum)
    const current = localStorage.getItem('selectedTenantId') || '';
    return meta.tenantId === current;
  } catch (_) {
    return true;
  }
};

/**
 * Invalida os caches locais com escopo de tenant (O.S., máquinas, operadores, locais).
 * Chamada na alternância de empresa pelo painel Master.
 * Preserva O.S. criadas offline ainda não sincronizadas (dados do usuário).
 */
export const clearTenantScopedCaches = async () => {
  try {
    await initDB();
    const cached = await getAllFromStore(STORES.WORK_ORDERS);
    for (const c of cached) {
      if (!(c.createdOffline && c.synced === false)) {
        await deleteById(STORES.WORK_ORDERS, c.id);
      }
    }
    await clearStore(STORES.MACHINES);
    await clearStore(STORES.OPERATORS);
    await clearStore(STORES.LOCATIONS);
    await deleteById(STORES.CACHE_META, 'cacheTenantId');
    console.log('🧹 Caches de tenant invalidados (troca de empresa)');
  } catch (e) {
    console.warn('Falha ao invalidar caches de tenant:', e);
  }
};

/**
 * Sincronizar dados do servidor para cache local
 */
export const syncDataFromServer = async (apiUrl, token) => {
  try {
    const headers = authHeaders();
    
    // Buscar e cachear máquinas
    const machinesRes = await fetch(`${apiUrl}/api/machines`, { headers });
    if (machinesRes.ok) {
      const machines = await machinesRes.json();
      await saveToStore(STORES.MACHINES, machines);
      console.log(`✅ ${machines.length} máquinas cacheadas`);
    }

    // Buscar e cachear operadores
    const operatorsRes = await fetch(`${apiUrl}/api/operators`, { headers });
    if (operatorsRes.ok) {
      const operators = await operatorsRes.json();
      await saveToStore(STORES.OPERATORS, operators);
      console.log(`✅ ${operators.length} operadores cacheados`);
    }

    // Buscar e cachear locais
    const locationsRes = await fetch(`${apiUrl}/api/locations`, { headers });
    if (locationsRes.ok) {
      const locations = await locationsRes.json();
      await saveToStore(STORES.LOCATIONS, locations);
      console.log(`✅ ${locations.length} locais cacheados`);
    }

    // Buscar e cachear O.S. — espelha o cache (abertas limitadas à janela de 60 dias)
    const ordersRes = await fetch(`${apiUrl}/api/work-orders`, { headers });
    if (ordersRes.ok) {
      const orders = await ordersRes.json();
      await mirrorWorkOrdersCache(orders);
      console.log(`✅ ${orders.length} O.S. espelhadas no cache offline`);
    }

    // Salvar timestamp do último sync
    await saveToStore(STORES.CACHE_META, {
      key: 'lastSync',
      timestamp: new Date().toISOString()
    });

    return { success: true };
  } catch (error) {
    console.error('Erro ao sincronizar dados:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Buscar dados com fallback offline
 */
export const fetchWithOfflineFallback = async (url, options = {}) => {
  try {
    const response = await fetch(url, options);
    if (response.ok) {
      return response;
    }
    throw new Error('Network response was not ok');
  } catch (error) {
    console.log('📴 Modo offline - usando dados locais');
    
    // Determinar qual store usar baseado na URL
    let storeName = null;
    if (url.includes('/api/machines')) storeName = STORES.MACHINES;
    else if (url.includes('/api/operators')) storeName = STORES.OPERATORS;
    else if (url.includes('/api/locations')) storeName = STORES.LOCATIONS;
    else if (url.includes('/api/work-orders')) storeName = STORES.WORK_ORDERS;
    
    if (storeName) {
      const data = await getAllFromStore(storeName);
      return {
        ok: true,
        offline: true,
        json: async () => data
      };
    }
    
    throw error;
  }
};

/**
 * Criar O.S. offline (salva localmente e agenda sync)
 */
export const createWorkOrderOffline = async (workOrderData) => {
  await initDB();
  
  // Gerar ID local temporário
  const localId = `local_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  
  const offlineOrder = {
    ...workOrderData,
    id: localId,
    synced: false,
    createdOffline: true,
    createdAt: new Date().toISOString(),
    status: workOrderData.end_hour_meter ? 'closed' : 'open'
  };
  
  // Salvar na store de O.S.
  await saveToStore(STORES.WORK_ORDERS, offlineOrder);
  
  // Adicionar à fila de sincronização
  await saveToStore(STORES.PENDING_SYNC, {
    type: 'work_order',
    action: 'create',
    data: workOrderData,
    localId: localId,
    createdAt: new Date().toISOString()
  });
  
  console.log('📴 O.S. salva offline:', localId);
  
  // Tentar registrar sync em background
  if ('serviceWorker' in navigator && 'SyncManager' in window) {
    const registration = await navigator.serviceWorker.ready;
    await registration.sync.register('sync-work-orders');
  }
  
  return offlineOrder;
};

/**
 * Atualizar O.S. offline (fechar O.S.)
 */
export const updateWorkOrderOffline = async (orderId, updateData) => {
  await initDB();
  
  // Buscar O.S. existente
  const existingOrder = await getById(STORES.WORK_ORDERS, orderId);
  
  if (!existingOrder) {
    throw new Error('O.S. não encontrada no cache local');
  }
  
  // Atualizar dados
  const updatedOrder = {
    ...existingOrder,
    ...updateData,
    synced: false,
    updatedAt: new Date().toISOString(),
    status: updateData.end_hour_meter ? 'closed' : existingOrder.status
  };
  
  // Salvar atualização
  await saveToStore(STORES.WORK_ORDERS, updatedOrder);
  
  // Se a O.S. original veio do servidor, adicionar à fila de sync
  if (!existingOrder.createdOffline) {
    await saveToStore(STORES.PENDING_SYNC, {
      type: 'work_order',
      action: 'update',
      orderId: orderId,
      data: updateData,
      createdAt: new Date().toISOString()
    });
  }
  
  console.log('📴 O.S. atualizada offline:', orderId);
  
  return updatedOrder;
};

/**
 * Obter itens pendentes de sincronização
 */
export const getPendingSyncItems = async () => {
  await initDB();
  return getAllFromStore(STORES.PENDING_SYNC);
};

/**
 * Remover item da fila de sincronização
 */
export const removePendingSyncItem = async (localId) => {
  await initDB();
  return deleteById(STORES.PENDING_SYNC, localId);
};

/**
 * Faz upload de fotos offline presentes nos dados da O.S. antes de sincronizar.
 *
 * Cobre TODOS os campos de foto: horímetro (operacional) + maintenance_photos (dict)
 * + materials_photo (manutenção / Bloco A). Cada foto tenta até MAX_PHOTO_RETRIES vezes
 * com backoff. REGRA DE OURO: em caso de falha, NUNCA descarta a base64 — mantém a foto
 * local e marca o item como ainda pendente para reenvio (manual ou automático).
 *
 * Retorna { data, allUploaded, failures }.
 */
const MAX_PHOTO_RETRIES = 3;

const uploadPhotoWithRetry = async (base64, folder, label) => {
  let lastErr;
  for (let attempt = 1; attempt <= MAX_PHOTO_RETRIES; attempt++) {
    try {
      const url = await uploadOfflinePhoto(base64, folder);
      console.log(`✅ Foto ${label} enviada (tentativa ${attempt}/${MAX_PHOTO_RETRIES})`);
      return url;
    } catch (err) {
      lastErr = err;
      console.warn(`⚠️ Falha no upload de ${label} (tentativa ${attempt}/${MAX_PHOTO_RETRIES}): ${err.message}`);
      if (attempt < MAX_PHOTO_RETRIES) {
        await new Promise((r) => setTimeout(r, attempt * 1500)); // backoff: 1.5s, 3s
      }
    }
  }
  throw lastErr;
};

const uploadPendingPhotos = async (data) => {
  const updatedData = { ...data };
  let allUploaded = true;
  const failures = [];

  const registrarFalha = (field, err) => {
    allUploaded = false;
    failures.push({ field, message: err.message, cloudinaryLimit: !!err.cloudinaryLimit });
  };

  // 1) Campos simples de horímetro (fluxo operacional — NÃO alterar comportamento de sucesso)
  for (const field of ['start_hour_meter_photo', 'end_hour_meter_photo']) {
    if (updatedData[field] && isOfflinePhoto(updatedData[field])) {
      try {
        updatedData[field] = await uploadPhotoWithRetry(updatedData[field], 'horimetros', field);
      } catch (err) {
        registrarFalha(field, err); // mantém base64 (Regra de Ouro)
      }
    }
  }

  // 2) maintenance_photos (dicionário de fotos da O.S. de manutenção — Bloco A)
  if (updatedData.maintenance_photos && typeof updatedData.maintenance_photos === 'object') {
    const mp = { ...updatedData.maintenance_photos };
    for (const key of Object.keys(mp)) {
      if (mp[key] && isOfflinePhoto(mp[key])) {
        try {
          mp[key] = await uploadPhotoWithRetry(mp[key], 'manutencao', `maintenance_photos.${key}`);
        } catch (err) {
          registrarFalha(`maintenance_photos.${key}`, err);
        }
      }
    }
    updatedData.maintenance_photos = mp;
  }

  // 3) materials_photo (foto do conjunto de peças/materiais — Bloco A Fase 2)
  if (updatedData.materials_photo && isOfflinePhoto(updatedData.materials_photo)) {
    try {
      updatedData.materials_photo = await uploadPhotoWithRetry(updatedData.materials_photo, 'manutencao', 'materials_photo');
    } catch (err) {
      registrarFalha('materials_photo', err);
    }
  }

  return { data: updatedData, allUploaded, failures };
};

/**
 * Sincronizar itens pendentes com o servidor
 */
export const syncPendingItems = async (apiUrl, token) => {
  const pendingItems = await getPendingSyncItems();

  if (pendingItems.length === 0) {
    console.log('✅ Nada para sincronizar');
    return { synced: 0, failed: 0, deferred: 0 };
  }

  console.log(`🔄 Sincronizando ${pendingItems.length} itens...`);

  let synced = 0;
  let failed = 0;
  let deferred = 0;
  let cloudinaryLimitHit = false;

  for (const item of pendingItems) {
    try {
      if (item.type === 'work_order') {
        // Upload de TODAS as fotos offline (com retry) antes de sincronizar
        const { data: syncData, allUploaded, failures } = await uploadPendingPhotos(item.data);

        // Persiste uploads bem-sucedidos para evitar reupload em nova tentativa
        if (JSON.stringify(syncData) !== JSON.stringify(item.data)) {
          item.data = syncData;
          await saveToStore(STORES.PENDING_SYNC, item);
          if (syncData.id) {
            const cached = (await getById(STORES.WORK_ORDERS, syncData.id)) || {};
            await saveToStore(STORES.WORK_ORDERS, { ...cached, ...syncData });
          }
        }

        // Regra de Ouro: se alguma foto não subiu, NÃO envia base64 ao servidor e
        // mantém o item na fila (badge "Salva localmente" permanece p/ reenvio).
        if (!allUploaded) {
          deferred++;
          if (failures.some((f) => f.cloudinaryLimit)) cloudinaryLimitHit = true;
          console.warn(`⏸️ O.S. adiada: ${failures.length} foto(s) ainda pendente(s) após ${MAX_PHOTO_RETRIES} tentativas.`);
          continue;
        }

        if (item.action === 'create') {
          const response = await fetch(`${apiUrl}/api/work-orders`, {
            method: 'POST',
            headers: authHeaders({ 'Content-Type': 'application/json' }),
            body: JSON.stringify(syncData)
          });

          if (response.ok) {
            const serverOrder = await response.json();
            await deleteById(STORES.WORK_ORDERS, item.localId);
            await saveToStore(STORES.WORK_ORDERS, { ...serverOrder, synced: true });
            await removePendingSyncItem(item.localId);
            synced++;
            console.log(`✅ O.S. ${item.localId} sincronizada como ${serverOrder.id}`);
          } else {
            failed++;
            console.error(`❌ Falha ao sincronizar O.S. ${item.localId}`);
          }
        } else if (item.action === 'update') {
          const response = await fetch(`${apiUrl}/api/work-orders/${item.orderId}`, {
            method: 'PUT',
            headers: authHeaders({ 'Content-Type': 'application/json' }),
            body: JSON.stringify(syncData)
          });

          if (response.ok) {
            const serverOrder = await response.json();
            await saveToStore(STORES.WORK_ORDERS, { ...serverOrder, synced: true });
            await removePendingSyncItem(item.localId);
            synced++;
            console.log(`✅ Atualização da O.S. ${item.orderId} sincronizada`);
          } else {
            failed++;
          }
        }
      }
    } catch (error) {
      failed++;
      console.error(`❌ Erro ao sincronizar item:`, error);
    }
  }

  return { synced, failed, deferred, cloudinaryLimitHit };
};

/**
 * Verificar se está online
 */
export const isOnline = () => navigator.onLine;

/**
 * Verificar se há itens pendentes de sync
 */
export const hasPendingSync = async () => {
  const items = await getPendingSyncItems();
  return items.length > 0;
};

/**
 * Obter timestamp do último sync
 */
export const getLastSyncTime = async () => {
  await initDB();
  const meta = await getById(STORES.CACHE_META, 'lastSync');
  return meta?.timestamp || null;
};

export { STORES };
